import React, { useState } from 'react';
import Header from 'components/dialogs/DialogHeader';
import Button from 'components/Button';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';
import FileCopySharpIcon from '@mui/icons-material/FileCopySharp';
import i18next from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { getShareLink } from 'api/analytics';
import { getReport } from 'api/client-phi';
import { openUrl } from 'api/general';
import styles from './styles.module.css';

function ShareAcquisitionDialog({ open, protocolToShare, orgToShare, onClose }) {
  const [acceptSharingTerms, setAcceptSharingTerms] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [clipboardLink, setClipboardLink] = useState('');

  const { t } = useTranslation();

  const SHARE_LINK_NOK = t('physio.physio-personal-share-box.input-box-share-link-copy-nok');
  const SHARE_LINK_OK = t('physio.physio-personal-share-box.input-box-share-link-copy-ok');

  const linkRef = React.createRef();

  const onGenerateLinkClick = () => {
    getShareLink({
      // 1. request para o physio API - get fileToken, confirmar qual eh a rota com lucas
      acquisition_id: protocolToShare,
      report_language: i18next.language,
      report_format: 'PDF',
    })
      .then((response) => {
        const { data, error } = response.data;

        if (!error) {
          setGeneratingLink(true);
          // 2. apos receber o tokenFile request report file using received token
          // 3. chamar lambda hospital - passar tokenFile. confirmar com lucas estrutura do json data
          getReport(orgToShare, data).then((response) => {
            // 4. hospital devolve o link do pdf no S3
            // open pre signed url
            const { data, error } = response.data;
            let timeOutCount = 0;

            const interval = setInterval(() => {
              openUrl(data)
                .then((res) => {
                  timeOutCount += 1; // cada tentativa equivale a 5s
                  if (res.status === 200) {
                    setGeneratingLink(false);

                    if (!error) {
                      setShareLink(data); // 5. abre o link do pdf em uma nova aba
                    }

                    clearInterval(interval);
                  }
                  if (timeOutCount >= 180) {
                    // atingindo um total de 15min (180 tentativas), da o timeOut
                    // eslint-disable-next-line no-alert
                    alert('Report error, please try again');
                    setGeneratingLink(false);
                    clearInterval(interval);
                  }
                })
                .catch(() => {});
            }, 2000);
          });
        }
      })
      .catch(() => {
        setGeneratingLink(false);
      });
  };

  const closeDialogHandler = (shared) => {
    setShareLink('');
    setClipboardLink('');
    onClose(shared);
  };

  const copyTextToClipboard = () => {
    if (shareLink === '') {
      setClipboardLink(SHARE_LINK_NOK);
      return;
    }
    linkRef.current.select();
    document.execCommand('copy');

    setClipboardLink(SHARE_LINK_OK);
  };

    return (
      <Dialog open={open}>
        <Header>{t('physio.physio-personal-share-box.title-box-share-report')}</Header>
        <DialogContent>
          <Typography variant="body2" color="error">
            <Trans i18nKey="physio.physio-personal-share-box.warning-share-link" />
          </Typography>

          <div className={styles.contentWrapper}>
            <FormControlLabel
              label={t('physio.physio-personal-share-box.input-box-share-link-terms')}
              control={
                <Checkbox
                  checked={acceptSharingTerms}
                  onClick={() => setAcceptSharingTerms(!acceptSharingTerms)}
                  data-testid="accept-sharing-terms-checkbox"
                  inputProps={{
                    'data-testid': 'accept-sharing-terms-checkbox-input',
                  }}
                />
              }
            />
            <Button
              variant="outlined"
              onClick={onGenerateLinkClick}
              disabled={!acceptSharingTerms}
              loading={generatingLink}
              data-testid="generate-link-button"
            >
              {t('physio.physio-personal-share-box.button-box-generate-link')}
            </Button>
          </div>

          <OutlinedInput
            name="url"
            inputRef={linkRef}
            value={shareLink}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={copyTextToClipboard} disabled={!acceptSharingTerms} aria-label="copy icon"
                  edge="end">
                  <FileCopySharpIcon />
                </IconButton>
              </InputAdornment>
            }
            disabled={!acceptSharingTerms}
            fullWidth
            inputProps={{ 'data-testid': 'url-input' }}
          />
          <Typography variant="body2" color="secondary">
            {clipboardLink}
          </Typography>
          <Typography variant="body2" color="primary.main" marginBottom="24px">
            {t('physio.physio-personal-share-box.text-box-share-link-pdf')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => closeDialogHandler(false)}>
            {t('physio.physio-personal-share-box.button-close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
}

export default ShareAcquisitionDialog;
